import React, { useEffect, useRef, useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadClinicStripe } from "@/lib/stripeClient";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CARD_STYLE = { hidePostalCode: true, style: { base: { fontSize: "16px", color: "#1f2937", "::placeholder": { color: "#9ca3af" } } } };

const Form = ({ clientId, onSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!consent) return toast.error("Tick the consent box first");
    if (!stripe || !elements) return toast.error("Card form isn't ready yet");
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/cards/intent`);
      const { error } = await stripe.confirmCardSetup(data.client_secret, { payment_method: { card: elements.getElement(CardElement) } });
      if (error) { toast.error(error.message || "Card couldn't be saved"); setBusy(false); return; }
      toast.success("Card saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the card"); setBusy(false); }
  };
  return (
    <div className="space-y-2.5" data-testid="add-card-inline">
      <div className="rounded-lg border border-input bg-background px-3 py-3.5"><CardElement options={CARD_STYLE} /></div>
      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-primary" data-testid="add-card-consent" /><span>The client has agreed for this card to be kept on file and charged for their bookings, deposits and no-show fees.</span></label>
      <div className="flex gap-2"><Button size="sm" className="rounded-lg" onClick={save} disabled={busy} data-testid="add-card-save">{busy ? "Saving…" : "Save card"}</Button><Button size="sm" variant="ghost" className="rounded-lg" onClick={onCancel} data-testid="add-card-cancel">Cancel</Button></div>
    </div>
  );
};

// Inline card capture (Stripe Elements + SetupIntent) — no email / phone needed, unlike the hosted Checkout page.
export const AddCardInline = ({ clientId, onSaved, onCancel }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [err, setErr] = useState("");
  const once = useRef(false);
  useEffect(() => {
    if (once.current) return; once.current = true;
    loadClinicStripe().then((p) => { if (p) setStripePromise(p); else setErr("Card payments aren't set up for this clinic yet."); }).catch(() => setErr("Couldn't load card payments."));
  }, []);
  if (err) return <div className="text-sm text-muted-foreground" data-testid="add-card-error">{err}</div>;
  if (!stripePromise) return <div className="text-sm text-muted-foreground">Loading card form…</div>;
  return <Elements stripe={stripePromise}><Form clientId={clientId} onSaved={onSaved} onCancel={onCancel} /></Elements>;
};
